import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import CompaniesAx   from 'app/actions/company-admin/companies';
import ToastAx       from 'app/actions/toast';
import Checkbox      from 'app/components/common/checkbox';
import Meta          from 'app/components/common/meta';
import Modal         from 'app/components/common/modal';
import StandardInput from 'app/components/common/standard-input';
import CadminLayout  from 'app/components/company-admin/layout/';
import PageLoading   from 'app/components/layout/page-loading';
import config        from 'app/config';
import utils         from 'app/helpers/utils';
import paths         from 'app/paths';
import prompts       from 'app/prompts';
import CadminSlx     from 'app/selectors/company-admin/';



class PageCadminSsoSettings extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      idpUrl: null,
      scimEnabled: null,
      showSettingsModal: false,
    };

    this.refIdpUrl = React.createRef();

    this.onClickSettings = this.onClickSettings.bind(this);
    this.onCloseSettings = this.onCloseSettings.bind(this);
    this.onChangeScimEnabled = this.onChangeScimEnabled.bind(this);
    this.onChangeIdpUrl = this.onChangeIdpUrl.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
  }

  get idpUrl() {
    return (this.state.idpUrl == null) ? this.props.company.samlIdpUrl : this.state.idpUrl;
  }
  get scimEnabled() {
    return _.isBoolean(this.state.scimEnabled) ? this.state.scimEnabled : this.props.company.scimEnabled;
  }
  get saveAttrs() {
    return {
      samlIdpUrl: (this.idpUrl || '').trim() || null,
      scimEnabled: this.scimEnabled,
    };
  }
  get canSubmit() {
    const {samlIdpUrl} = this.saveAttrs;
    const hasValidIdp = (samlIdpUrl === null) || utils.isUrl(samlIdpUrl);
    if (!hasValidIdp) return false;
    if (this.props.updatePending) return false;
    return true;
  }

  onClickSettings() {
    this.setState({showSettingsModal: true, scimEnabled: null, idpUrl: null});
  }
  onCloseSettings() {
    this.setState({showSettingsModal: false});
  }

  onChangeScimEnabled(event) {
    const scimEnabled = event.target.checked;
    this.setState({scimEnabled});
  }
  onChangeIdpUrl(event) {
    const idpUrl = event.target.value;
    this.setState({idpUrl});
  }

  async onClickSave() {
    const msg = `Updating these settings will affect your employees' ability to access Millie.\nPlease only proceed if you know what you're doing.\nContact tech@milliegiving.com if you need assistance.`;
    const didConfirm = await prompts.confirm({msg, confirmBtnLabel: 'Save Changes'});
    if (didConfirm) {
      this.props.updateSso(this.props.company.id, this.saveAttrs).then(() => {
        this.setState({showSettingsModal: false});
        this.props.toastSuccess('Settings updated.');
      });
    }
  }

  renderScimEnabledBadge() {
    let className, text;
    if (this.props.company.scimEnabled) {
      className = 'active';
      text = 'active';
    }
    else {
      className = 'inactive';
      text = 'off';
    }

    return (
      <span className={`ca-sso-scim-badge ${className}`}>{text}</span>
    );
  }

  renderSettingsModal() {
    if (!this.state.showSettingsModal) return null;
    
    return (
      <Modal className="bform" onClose={this.onCloseSettings}>
        <h1 className="bform-h1">Settings</h1>

        <h2 className="bform-h2">SAML SSO</h2>
        <label className="bform-h3">IdP Metadata URL</label>
        <StandardInput value={this.idpUrl} onChange={this.onChangeIdpUrl} label="https://your-org.okta.com/..." name="idpUrl" />

        <h2 className="bform-h2">SCIM Provisioning</h2>
        <div className="_flex _justify-start _align-center _gap-6">
          <Checkbox checked={this.scimEnabled} onChange={this.onChangeScimEnabled} isToggle id="scim-enabled-cb" />
          <label htmlFor="scim-enabled-cb">{this.scimEnabled ? 'Enabled' : 'Disabled'}</label>
        </div>

        <div className="bform-actions">
          <div className="bform-actions-right">
            <button className="btn blue" onClick={this.onClickSave} disabled={!this.canSubmit}>{this.props.updatePending ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const {company} = this.props;
    if (!company) return <PageLoading />;
    const urlAcs = `${config.baseUrl}${paths.samlAcs(company.slug)}`;
    const urlSpMetadata = `${config.baseUrl}${paths.samlMetadata(company.slug)}`;
    const urlSpLogin = `${config.baseUrl}${paths.samlLogin(company.slug)}`;
    const domains = company.domains || [];
    const urlScim = `${config.baseUrl}${paths.scimBase(company.slug)}`;

    return (
      <CadminLayout className="ca-sso" company={company} activeItem="sso">
        <Meta title="SSO Settings | Millie" />
        <div className="ca-box">
          <div className="ca-box-header">
            <h1 className="ca-box-header-title">SSO (SAML)</h1>
            <div className="ca-box-header-controls">
              <button className="btn blue" onClick={this.onClickSettings}>Settings</button>
            </div>
          </div>
          <div className="ca-box-body">
            <h2>Register Millie with IdP</h2>
            <table className="ca-keyvals"><tbody>
              <tr>
                <th>Company Slug</th>
                <td><code>{company.slug}</code></td>
              </tr>
              <tr>
                <th>SP Metadata URL</th>
                <td><code>{urlSpMetadata}</code></td>
              </tr>
              <tr>
                <th>ACS URL</th>
                <td><code>{urlAcs}</code></td>
              </tr>
              <tr>
                <th>SP Login URL</th>
                <td><code>{urlSpLogin}</code></td>
              </tr>
              <tr>
                <th>Required Attributes</th>
                <td>
                  <code>id</code> or <code>_id</code>, <code>email</code>, <code>firstName</code>, and <code>lastName</code>
                  <p>Millie uses <code>id</code> or <code>_id</code> as the primary identifier for employees.</p>
                </td>
              </tr>
              <tr>
                <th>Optional Attributes</th>
                <td>
                  <code>profileImage</code>
                </td>
              </tr>
            </tbody></table>

            <br /><br />
            <h2>Register IdP with Millie</h2>
            <label htmlFor="input-saml-idp-url">IdP Metadata URL</label>
            <StandardInput id="input-saml-idp-url" disabled defaultValue={company.samlIdpUrl} name="samlIdpUrl" label="IdP Metadata URL" ref={this.refIdpUrl} />

            <br /><br />
            <h2>Other Settings</h2>
            <table className="ca-keyvals"><tbody>
              <tr>
                <th>Email Domains</th>
                <td>
                  <div className="_flex _gap-6 _flex-wrap _justify-start">
                    {domains.map((domain, i) => {
                      return <code key={domain}>{domain}</code>;
                    })}
                  </div>
                  <p>Millie uses these domains to help direct your employees to the correct place to sign in.</p>
                  <p>To change these, please email team@milliegiving.com</p>
                </td>
              </tr>
            </tbody></table>
          </div>
        </div>

        <div className="ca-box">
          <div className="ca-box-header">
            <h1 className="ca-box-header-title">
              Provisioning (SCIM 2.0) {this.renderScimEnabledBadge()}
            </h1>
            <div className="ca-box-header-controls">
            </div>
          </div>

          <div className="ca-box-body">
            <h2>Connection Details</h2>
            <table className="ca-keyvals"><tbody>
              <tr>
                <th>Base URL</th>
                <td><code>{urlScim}</code></td>
              </tr>
              <tr>
                <th>OAuth Bearer Token</th>
                <td><code>{company.scimAuthToken || ''}</code></td>
              </tr>
            </tbody></table>
          </div>
        </div>
        {this.renderSettingsModal()}
      </CadminLayout>
    );
  }

}

const stateToProps = (state) => ({
  company: CadminSlx.company(state),
  updatePending: CadminSlx.updateSsoPending(state),
});

const dispatchToProps = (dispatch) => ({
  updateSso: (companyId, attrs) => dispatch(CompaniesAx.updateSso(companyId, attrs)),
  toastSuccess: (msg) => dispatch(ToastAx.success(msg)),
});

export default connect(stateToProps, dispatchToProps)(PageCadminSsoSettings);
